import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, mondayOf, planDays as buildDays, type PlanItem, type RangeDays } from '@jisy/shared';
import { apiRequest } from '../api/client.js';
import { accountQuery, planOwnersQuery, planViewQuery, projectsQuery } from '../api/queries.js';
import { CreateIssueModal } from '../components/CreateIssueModal.js';
import { IssuePicker } from '../components/IssuePicker.js';
import { isPlanItemDone, PlanGrid } from '../components/PlanGrid.js';
import { TableSkeleton } from '../components/States.js';
import { todayIso } from '../lib/format.js';
import { localeTag } from '../i18n/index.js';
import styles from './PlanningPage.module.css';

/**
 * Режим планирования: сетка на неделю или две, подбор задач, загрузка по дням.
 *
 * Отметки уходят на сервер пакетом сразу после протягивания, а в Jira — отложенно, очередью
 * с дебаунсом: протягивание по десяти дням не должно превращаться в десять запросов наружу.
 */

/**
 * Выбор «7 или 14 дней» помнится в браузере. Настройка тенанта задаёт, с чего режим открывается
 * впервые, но она общая на всех, а ширину сетки каждый подбирает под свой экран — и ждёт увидеть
 * её же на следующий день. Личный выбор поэтому перекрывает настройку и живёт на устройстве.
 */
const RANGE_STORAGE_KEY = 'jisy.planDays';

function storedRange(): RangeDays | null {
  const stored = window.localStorage.getItem(RANGE_STORAGE_KEY);
  if (stored === '7') return 7;
  if (stored === '14') return 14;
  return null;
}

/**
 * Порядок строк. «Вручную» — сохранённый в базе порядок перетаскивания, остальное — только вид:
 * позиции при сортировке не переписываются, поэтому вернуться к своему порядку можно в любой
 * момент. Пока сортировка включена, перетаскивание отключено — иначе перенос записал бы позиции,
 * которых на экране не видно.
 */
const SORT_STORAGE_KEY = 'jisy.planSort';

const PLAN_SORTS = ['manual', 'section', 'priority'] as const;
type PlanSort = (typeof PLAN_SORTS)[number];

function storedSort(): PlanSort {
  const stored = window.localStorage.getItem(SORT_STORAGE_KEY);
  return PLAN_SORTS.find((value) => value === stored) ?? 'manual';
}

/** Задача без приоритета встаёт в конец своей группы: неизвестная важность — не высшая. */
function rankOf(item: PlanItem): number {
  return item.issue?.priority?.rank ?? Number.MAX_SAFE_INTEGER;
}

/** Строка без раздела — тоже в конец: пустое название иначе оказалось бы первым по алфавиту. */
function sectionKeyOf(item: PlanItem): string {
  return item.issue?.projectName ?? '';
}

function compareBySection(first: PlanItem, second: PlanItem): number {
  const left = sectionKeyOf(first);
  const right = sectionKeyOf(second);
  if (left === right) return 0;
  if (left === '') return 1;
  if (right === '') return -1;
  return left.localeCompare(right, localeTag());
}

export function PlanningPage({
  siteUrl,
  onOpenIssue,
}: {
  siteUrl: string;
  onOpenIssue: (issueKey: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [start, setStart] = useState(() => mondayOf(todayIso()));
  const [chosenDays, setChosenDays] = useState<RangeDays | null>(storedRange);
  const [sort, setSort] = useState<PlanSort>(storedSort);
  const [owner, setOwner] = useState<string>('me');
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Аккаунт уже в кэше: без него не отрисовано и само приложение, поэтому диапазон готов сразу.
  const account = useQuery(accountQuery);
  const days: RangeDays = chosenDays ?? account.data?.settings.defaultRangeDays ?? 7;

  const changeDays = useCallback((next: RangeDays) => {
    setChosenDays(next);
    window.localStorage.setItem(RANGE_STORAGE_KEY, String(next));
  }, []);

  const changeSort = useCallback((next: PlanSort) => {
    setSort(next);
    window.localStorage.setItem(SORT_STORAGE_KEY, next);
  }, []);

  const view = useQuery(planViewQuery({ owner, start, days, q: search }));
  const owners = useQuery(planOwnersQuery);
  const projects = useQuery(projectsQuery);

  const dayList = useMemo(() => buildDays(start, days), [start, days]);
  /**
   * Выполненные задачи опускаются в конец: план читается сверху вниз как «что осталось».
   * Это правило старше выбранной сортировки и действует всегда — выполненное собрано отдельным
   * блоком внизу, а направление или важность упорядочивают строки уже внутри каждой группы.
   *
   * Сортировка живёт здесь, а не в сетке, потому что перенос строк считает позиции по этому
   * же массиву — иначе перетаскивание записало бы порядок, которого пользователь не видит.
   * Сортировка устойчива, поэтому при равенстве строки остаются в порядке, заданном вручную.
   */
  const items: PlanItem[] = useMemo(() => {
    const all = view.data?.plans.flatMap((plan) => plan.items) ?? [];

    return all.sort((first, second) => {
      const byDone = Number(isPlanItemDone(first)) - Number(isPlanItemDone(second));
      if (byDone !== 0) return byDone;

      if (sort === 'section') return compareBySection(first, second);
      if (sort === 'priority') return rankOf(first) - rankOf(second);
      return 0;
    });
  }, [view.data, sort]);

  const planId = view.data?.plans[0]?.id ?? '';
  const editable = view.data?.editable ?? false;

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['plan-view'] });
  }, [queryClient]);

  const ensurePlan = useMutation({
    mutationFn: () =>
      apiRequest<{ planId: string }>('/api/plans', {
        method: 'POST',
        body: { start, days },
      }),
  });

  const setDaysMutation = useMutation({
    mutationFn: (input: { itemId: string; changes: Array<{ day: string; marked: boolean }> }) =>
      apiRequest(`/api/plans/${planId}/items/${input.itemId}/days`, {
        method: 'PUT',
        body: { days: input.changes },
      }),
    onSettled: invalidate,
  });

  const resolveConflictMutation = useMutation({
    mutationFn: (input: { itemId: string; resolution: 'local' | 'remote' }) =>
      apiRequest(`/api/plans/${planId}/items/${input.itemId}/conflict`, {
        method: 'POST',
        body: { resolution: input.resolution },
      }),
    onSettled: invalidate,
  });

  const removeItemMutation = useMutation({
    mutationFn: (itemId: string) =>
      apiRequest(`/api/plans/${planId}/items/${itemId}`, { method: 'DELETE' }),
    onSettled: invalidate,
  });

  /**
   * Перенос строки. Позиции пересчитываются на клиенте и уходят по одной: строк в плане
   * десятки, а не тысячи, и отдельный пакетный маршрут ради этого не нужен.
   */
  const reorderMutation = useMutation({
    mutationFn: async (input: { itemId: string; beforeItemId: string | null }) => {
      const order = items.map((item) => item.id).filter((id) => id !== input.itemId);
      const at = input.beforeItemId ? order.indexOf(input.beforeItemId) : order.length;
      order.splice(at < 0 ? order.length : at, 0, input.itemId);

      await Promise.all(
        order.map((id, position) =>
          apiRequest(`/api/plans/${planId}/items/${id}`, {
            method: 'PATCH',
            body: { position },
          }),
        ),
      );
    },
    onSettled: invalidate,
  });

  const addItemsMutation = useMutation({
    mutationFn: async (issueKeys: string[]) => {
      // Плана может ещё не быть: он заводится при первом добавлении, а не при открытии экрана.
      const id = planId || (await ensurePlan.mutateAsync()).planId;
      return apiRequest(`/api/plans/${id}/items`, { method: 'POST', body: { issueKeys } });
    },
    onSettled: invalidate,
  });

  const rangeLabel = useMemo(() => {
    const from = new Date(`${start}T00:00:00`);
    const to = new Date(`${addDays(start, days - 1)}T00:00:00`);
    const format = new Intl.DateTimeFormat(localeTag(), { day: '2-digit', month: '2-digit' });
    return `${format.format(from)}–${format.format(to)}`;
  }, [start, days]);

  const totalMarks = items.reduce((sum, item) => sum + item.days.length, 0);
  const peak = Math.max(0, ...(view.data?.load ?? []).map((entry) => entry.load));

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.searchField}>
          <span className={styles.searchIcon} aria-hidden="true">
            ⌕
          </span>
          <input
            className={styles.searchInput}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('plan.search')}
            aria-label={t('plan.search')}
          />
        </div>

        <select
          className={styles.select}
          value={owner}
          onChange={(event) => setOwner(event.target.value)}
          aria-label={t('plan.owner')}
        >
          <option value="me">{t('plan.ownerMe')}</option>
          <option value="all">{t('plan.ownerAll')}</option>
          {(owners.data?.owners ?? []).map((person) => (
            <option value={person.accountId} key={person.accountId}>
              {person.displayName}
            </option>
          ))}
        </select>

        <select
          className={styles.select}
          value={sort}
          onChange={(event) => changeSort(sortOf(event.target.value))}
          aria-label={t('plan.sort')}
        >
          <option value="manual">{t('plan.sortManual')}</option>
          <option value="section">{t('plan.sortSection')}</option>
          <option value="priority">{t('plan.sortPriority')}</option>
        </select>

        <div className={styles.spacer} />

        <div className={styles.rangeNav}>
          <button
            type="button"
            className={styles.navButton}
            onClick={() => setStart(addDays(start, -7))}
            aria-label={t('plan.previousWeek')}
          >
            ‹
          </button>
          <span className={`mono ${styles.rangeLabel}`}>{rangeLabel}</span>
          <button
            type="button"
            className={styles.navButton}
            onClick={() => setStart(addDays(start, 7))}
            aria-label={t('plan.nextWeek')}
          >
            ›
          </button>
        </div>

        <div className={styles.segmented}>
          <button
            type="button"
            className={days === 7 ? styles.segmentActive : styles.segment}
            onClick={() => changeDays(7)}
          >
            {t('plan.days7')}
          </button>
          <button
            type="button"
            className={days === 14 ? styles.segmentActive : styles.segment}
            onClick={() => changeDays(14)}
          >
            {t('plan.days14')}
          </button>
        </div>

        <button
          type="button"
          className={styles.createButton}
          onClick={() => setCreating(true)}
          disabled={!editable || (projects.data?.projects.length ?? 0) === 0}
          title={editable ? undefined : t('plan.readOnly')}
        >
          {t('create.action')}
        </button>

        <button
          type="button"
          className={styles.addButton}
          onClick={() => setPickerOpen(true)}
          disabled={!editable}
          title={editable ? undefined : t('plan.readOnly')}
        >
          {t('plan.addIssues')}
        </button>
      </div>

      {view.isPending && <TableSkeleton rows={6} />}

      {!view.isPending && items.length === 0 && (
        <div className={styles.empty}>
          <div className={styles.emptyGrid} aria-hidden="true">
            {dayList.map((day) => (
              <div key={day} className={styles.emptyColumn} />
            ))}
          </div>
          <div className={styles.emptyContent}>
            <div className={styles.emptyTitle}>{t('plan.emptyTitle')}</div>
            <p className={styles.emptyBody}>{t('plan.emptyBody')}</p>
            <button
              type="button"
              className={styles.addButton}
              onClick={() => setPickerOpen(true)}
              disabled={!editable}
            >
              {t('plan.addIssues')}
            </button>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <PlanGrid
          items={items}
          days={dayList}
          load={view.data?.load ?? []}
          editable={editable}
          today={todayIso()}
          onChangeDays={(itemId, changes) => setDaysMutation.mutate({ itemId, changes })}
          onOpenIssue={onOpenIssue}
          onResolveConflict={(itemId, resolution) =>
            resolveConflictMutation.mutate({ itemId, resolution })
          }
          onRemove={(itemId) => removeItemMutation.mutate(itemId)}
          onReorder={(itemId, beforeItemId) => reorderMutation.mutate({ itemId, beforeItemId })}
          reorderable={sort === 'manual'}
        />
      )}

      <div className={styles.footer}>
        <span className={styles.footerText}>
          {t('plan.summary', { issues: items.length, marks: totalMarks, peak })}
        </span>
        <div className={styles.spacer} />
        <span className={`mono ${styles.hints}`}>{t('plan.hints')}</span>
      </div>

      {creating && (
        <CreateIssueModal
          projects={projects.data?.projects ?? []}
          // В плане задачи из разных разделов, поэтому раздел выбирается в самой форме.
          initialProjectId={null}
          siteUrl={siteUrl}
          onClose={() => setCreating(false)}
          onCreated={(issue) => {
            setCreating(false);
            // Создали из планирования — значит задачу планируют: строка сразу встаёт в сетку.
            addItemsMutation.mutate([issue.key]);
          }}
        />
      )}

      {pickerOpen && (
        <IssuePicker
          rangeLabel={rangeLabel}
          alreadyInPlan={items.map((item) => item.issueKey)}
          projects={projects.data?.projects ?? []}
          onClose={() => setPickerOpen(false)}
          onSubmit={(issueKeys) => {
            addItemsMutation.mutate(issueKeys);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

/** Значение из выпадающего списка — строка; неизвестное считаем ручным порядком. */
function sortOf(value: string): PlanSort {
  return PLAN_SORTS.find((sort) => sort === value) ?? 'manual';
}
